import { ActionIcon, Box, Button, Divider, Flex } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { ReactNode, useState } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useDeleteEntities } from 'src/data-capture/merge-review/useDeleteEntities';
import { EntityFields } from 'src/UI/dynamicUI/types';
import { useEntityForm } from '../EntityFormProvider';
import { ListItemDeletionModal } from './ListItemDeletionModal';

type ProfileFormListProps = {
  entityType: string;
  addButtonLabel: string;
  defaultFields: EntityFields;
  children: (index: number) => ReactNode;
};

export function ProfileFormList({
  entityType,
  addButtonLabel,
  defaultFields,
  children,
}: ProfileFormListProps) {
  const { clientId } = useCurrentClient();
  const { isEditing, setIsEditing, form, entities } = useEntityForm();
  const { deleteEntityByTypeAndId } = useDeleteEntities();
  const [opened, { open, close }] = useDisclosure(false);
  const [deletionIndex, setDeletionIndex] = useState<number | null>(null);

  const entityData = entities[entityType];
  if (!entityData || !Array.isArray(entityData)) {
    return null;
  }

  const formListValues = form.getValues()[entityType];
  if (!Array.isArray(formListValues)) {
    return null;
  }

  const handleDeletePress = (index: number) => {
    setDeletionIndex(index);
    open();
  };

  return (
    <Box>
      {deletionIndex !== null && (
        <ListItemDeletionModal
          form={form}
          entityType={entityType}
          index={deletionIndex}
          opened={opened}
          close={close}
          clientId={clientId}
          deleteEntityByTypeAndId={deleteEntityByTypeAndId}
        />
      )}
      {formListValues.map((entity, entityIndex) => (
        <Box key={entityIndex} data-testid={`entity-list-item-${entityIndex}`}>
          <Flex mb={'md'} align={'start'} direction={'row'}>
            <Box flex={1}>{children(entityIndex)}</Box>
            {isEditing && (
              <ActionIcon
                aria-label="Remove"
                color="gray.2"
                ml={'md'}
                mt={'xl'}
                w={32}
                h={32}
                onClick={() => {
                  handleDeletePress(entityIndex);
                }}
              >
                <IconTrash
                  color="var(--mantine-color-gray-7)"
                  stroke={1.5}
                  size={20}
                  data-testid="delete-button"
                />
              </ActionIcon>
            )}
          </Flex>
          {entityIndex !== formListValues.length - 1 && <Divider my="xxl" />}
        </Box>
      ))}
      <Box>
        <Button
          variant="pill"
          leftSection={<IconPlus size={16} />}
          data-testid="add-button"
          onClick={() => {
            form.insertListItem(entityType, defaultFields);
            setIsEditing(true);
          }}
        >
          {addButtonLabel}
        </Button>
      </Box>
    </Box>
  );
}
